// Phase 13 — Country selector for fighter nationality.
// List focuses on West/Central African countries (BUFL's home region) + a
// pragmatic global set. Each entry: iso2 code + emoji flag + French name.

export const COUNTRIES = [
  // Afrique de l'Ouest — priorité BUFL
  { code: "BF", flag: "🇧🇫", name: "Burkina Faso" },
  { code: "CI", flag: "🇨🇮", name: "Côte d'Ivoire" },
  { code: "ML", flag: "🇲🇱", name: "Mali" },
  { code: "SN", flag: "🇸🇳", name: "Sénégal" },
  { code: "NE", flag: "🇳🇪", name: "Niger" },
  { code: "GN", flag: "🇬🇳", name: "Guinée" },
  { code: "TG", flag: "🇹🇬", name: "Togo" },
  { code: "BJ", flag: "🇧🇯", name: "Bénin" },
  { code: "GH", flag: "🇬🇭", name: "Ghana" },
  { code: "NG", flag: "🇳🇬", name: "Nigeria" },
  { code: "LR", flag: "🇱🇷", name: "Libéria" },
  { code: "SL", flag: "🇸🇱", name: "Sierra Leone" },
  { code: "GM", flag: "🇬🇲", name: "Gambie" },
  { code: "GW", flag: "🇬🇼", name: "Guinée-Bissau" },
  { code: "CV", flag: "🇨🇻", name: "Cap-Vert" },
  { code: "MR", flag: "🇲🇷", name: "Mauritanie" },
  // Afrique centrale
  { code: "CM", flag: "🇨🇲", name: "Cameroun" },
  { code: "CF", flag: "🇨🇫", name: "Centrafrique" },
  { code: "TD", flag: "🇹🇩", name: "Tchad" },
  { code: "CG", flag: "🇨🇬", name: "Congo" },
  { code: "CD", flag: "🇨🇩", name: "RD Congo" },
  { code: "GA", flag: "🇬🇦", name: "Gabon" },
  { code: "GQ", flag: "🇬🇶", name: "Guinée équatoriale" },
  // Afrique du Nord + Est + Sud (grands pays MMA africains)
  { code: "MA", flag: "🇲🇦", name: "Maroc" },
  { code: "DZ", flag: "🇩🇿", name: "Algérie" },
  { code: "TN", flag: "🇹🇳", name: "Tunisie" },
  { code: "EG", flag: "🇪🇬", name: "Égypte" },
  { code: "LY", flag: "🇱🇾", name: "Libye" },
  { code: "SD", flag: "🇸🇩", name: "Soudan" },
  { code: "ET", flag: "🇪🇹", name: "Éthiopie" },
  { code: "KE", flag: "🇰🇪", name: "Kenya" },
  { code: "UG", flag: "🇺🇬", name: "Ouganda" },
  { code: "TZ", flag: "🇹🇿", name: "Tanzanie" },
  { code: "RW", flag: "🇷🇼", name: "Rwanda" },
  { code: "BI", flag: "🇧🇮", name: "Burundi" },
  { code: "SO", flag: "🇸🇴", name: "Somalie" },
  { code: "ZA", flag: "🇿🇦", name: "Afrique du Sud" },
  { code: "AO", flag: "🇦🇴", name: "Angola" },
  { code: "MZ", flag: "🇲🇿", name: "Mozambique" },
  { code: "MG", flag: "🇲🇬", name: "Madagascar" },
  // Europe / Amérique / Asie — combattants internationaux
  { code: "FR", flag: "🇫🇷", name: "France" },
  { code: "BE", flag: "🇧🇪", name: "Belgique" },
  { code: "GB", flag: "🇬🇧", name: "Royaume-Uni" },
  { code: "IT", flag: "🇮🇹", name: "Italie" },
  { code: "ES", flag: "🇪🇸", name: "Espagne" },
  { code: "PT", flag: "🇵🇹", name: "Portugal" },
  { code: "DE", flag: "🇩🇪", name: "Allemagne" },
  { code: "US", flag: "🇺🇸", name: "États-Unis" },
  { code: "CA", flag: "🇨🇦", name: "Canada" },
  { code: "BR", flag: "🇧🇷", name: "Brésil" },
  { code: "MX", flag: "🇲🇽", name: "Mexique" },
  { code: "RU", flag: "🇷🇺", name: "Russie" },
  { code: "TR", flag: "🇹🇷", name: "Turquie" },
  { code: "JP", flag: "🇯🇵", name: "Japon" },
  { code: "TH", flag: "🇹🇭", name: "Thaïlande" },
  { code: "CN", flag: "🇨🇳", name: "Chine" },
  { code: "PH", flag: "🇵🇭", name: "Philippines" },
  { code: "AU", flag: "🇦🇺", name: "Australie" },
  { code: "NZ", flag: "🇳🇿", name: "Nouvelle-Zélande" },
];

export const findCountry = (code) => COUNTRIES.find((c) => c.code === code) || null;

// Phase 13 — Public display helper.
// Fighter data stores `nationality` as an ISO2 code (e.g. "BF", "CI"). The
// public UI must show the French country name — never the raw code.
// Fallback order:
//   1. Lookup by ISO2 code (canonical form).
//   2. Lookup by French name in case the DB still holds the legacy full name
//      (some prod rows may predate Phase 13's ISO2 canonicalisation).
//   3. Return whatever the DB says as-is (never crash).
export const nationalityLabel = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const byCode = COUNTRIES.find((c) => c.code === s.toUpperCase());
  if (byCode) return byCode.name;
  const byName = COUNTRIES.find(
    (c) => c.name.toLowerCase() === s.toLowerCase(),
  );
  if (byName) return byName.name;
  return s;
};

// Same, but returns "🇧🇫 Burkina Faso" when both flag + name are available.
export const nationalityWithFlag = (raw, flag) => {
  const name = nationalityLabel(raw);
  const parts = [flag, name].filter(Boolean);
  return parts.length ? parts.join(" ") : null;
};
